<template>
  <aside>
    <div class="d-flex flex-column align-items-center align-items-sm-start h-100 aside_content">
      <a :href="dashboardLink" class="d-flex align-items-center w-100 mb-4">
        <span class="aside_logo mx-auto">
          <img :src="logoUrl" class="img" style="max-height: 80px; width: 100%; object-fit: contain;" />
        </span>
        <!-- <span class="aside_small_logo mx-auto"><img src="/assets/front_end/assets/images/small_logo.svg" class="img"></span> -->
      </a>
      <ul class="aside_menu custom-ul nav nav-pills w-100" id="menu">
        <SidebarItem v-for="(item, index) in visibleLinks" :link="item" :index="index" :key="item.label + index"
          :links="links" :fav_list="fav_list" :history_list="history_list" @addToFav="addToFav"
          @removeFromFav="removeFromFav" @getUserFavourites="getUserFavourites"
          @getUserLinkHistories="getUserLinkHistories" @handleChangeFavourites="handleChangeFavourites"
          @removeSearchResult="removeSearchResult" @addToLinkHistory="addToLinkHistory" />
      </ul>

      <div class="d-flex align-items-center justify-content-end flex-column w-100 aside_footer_img mt-5">
        <span class="aside_footer_logo">
          <img src="/assets/images/footerlogo.svg" class="mx-auto" />
          <router-link 
            v-if="version" 
            to="/updates" 
            style="text-align: center; font-size: 14px; margin-top: 5px; display: block; color: #1d5166; text-decoration: none; transition: all 0.3s ease; cursor: pointer;"
            class="version-link"
          >
            {{ version }}
          </router-link>
          <p v-else style="text-align: center; font-size: 14px; margin-top: 5px">
            ...
          </p>
        </span>
      </div>

      <!-- aside open icon (mobile view) -->
      <div class="aside_open_icon">
        <i class="fas fa-angle-double-left"></i>
      </div>
    </div>
  </aside>
</template>

<script>
import eBus from "@/event-bus";
import Favorite from "@api/favorite.api";
import LinkHistory from "@api/link_history.api";
import Language from "@api/language.api";
import SidebarItem from "./SidebarItem.vue";
import StorageService from "@services/storage.service";
import ApiService from "@services/api.service";
import { ref, onMounted } from "vue";
import VersionApi from "@/api/version.api";
import axios from "axios";

import { mapState, mapGetters } from "vuex";

const version = ref(null);

export default {
  components: {
    SidebarItem
  },

  computed: {
    ...mapState("users", ["user"]),
    ...mapGetters(["isSuperadminLoggedSchool"]),



    activeLanguages() {
      return this.availableLanguages.filter(l => l.is_active);
    },

    currentLanguageNativeName() {
      const current = this.availableLanguages.find(l => l.code === this.currentLocale);
      return current ? current.native_name : (this.currentLocale.startsWith('ar') ? 'العربية' : 'English');
    },
    ...mapGetters("settings", ["sidebarLogo"]),

    logoUrl() {
      if (this.user?.school?.logo) {
        // Assuming logo is a string path, ensure it has /storage/ or full path
        if (this.user.school.logo.startsWith('http') || this.user.school.logo.includes('assets/')) {
          return this.user.school.logo.startsWith('assets/') ? `/${this.user.school.logo}` : this.user.school.logo;
        }
        return `/storage/${this.user.school.logo}`;
      }

      if (this.sidebarLogo) {
        return this.sidebarLogo;
      }

      return "/assets/images/main_logo.svg";
    },

    dashboardLink() {
      // If Super Admin is accessing a school (Impersonating), go to root (School Dashboard)
      if (this.isSuperadminLoggedSchool) {
        return '/';
      }

      // If Super Admin (and not accessing school), go to Super Admin Dashboard
      if (this.hasRole(this.ROLES.SYSTEMADMINS) && !this.user.school) {
        return '/super-admin/dashboard';
      }

      // Default for others
      return '/';
    },

    // NEW: Filter links to only show parents with visible children
    visibleLinks() {
      if (!this.links) return [];

      return this.links.filter(link => {
        // Always show search type
        if (link.type === 'search') {
          return true;
        }


        // Check for devOnly property
        if (link.devOnly && window.appEnv !== 'local') {
          return false;
        }

        // For group type, check if any children are visible
        if (link.type === 'group' || link.children) {
          return this.hasVisibleChildren(link.children);
        }

        // For regular links, check their own permission
        return link.permission ? this.can(link.permission) : true;
      });
    },
  },

  props: {
    fav_list: {
      type: Array,
      required: false,
    },
  },

  data() {
    return {
      links: null,
      isOpen: false,
      openGroups: {},
      version: null,
      availableLanguages: [],

      // fav_list: [],
      history_list: [],
      currentLocale: 'ar', // Default fallback, updated in created()

      systemAdminLinks: this.$mods.side_menu.systemAdminLinks,

      schoolLinks: this.$mods.side_menu.schoolLinks,

      teachersLinks: this.$mods.side_menu.teachersLinks,

      hodLinks: [
        {
          label: "dashboard",
          path: "/hod-dashboard",
          icon: "schoolna-sid-home-icon",
          permission: ""
        },
        {
          label: "trips",
          path: "/hod/trips",
          icon: "schoolna-tran-trips",
          permission: "VIEW_TRIPS"
        }
      ],

      nurseLinks: [
        {
          type: "group",
          label: "attendance",
          icon: "fa-book",
          children: [
            {
              label: "period_attendance",
              path: "/period_attendance",
              icon: "fa-book",
              permission: "VIEW_ATTENDANCES",
            },
            {
              label: "home_room_attendance",
              path: "/home_room_attendance",
              icon: "fa-book",
              permission: "VIEW_ATTENDANCES",
            },
            {
              label: "attendance_reports",
              path: "/attendance-reports",
              icon: "fa-user-tie",
              permission: "VIEW_ATTENDANCE_REPORTS",
            },
          ],
        },
      ],
    };
  },

  created() {
    // Load saved locale first
    this.loadSavedLocale();
    this.currentLocale = this.Lang.getLocale();

    this.getUserLinkHistories();

    // Initial setup
    this.resetSidebarState();
    this.fetchVersion();
    this.fetchLanguages();

    // Listen for route changes
    this.$router.beforeEach((to, from, next) => {
      eBus.$emit("school-changed");
      next();
    });

    // Listen at superadmin logged as school admin
    eBus.$on("superAdminAsSchoolAdmin", () => {
      if (this.user?.teacher) {
        this.links = this.teachersLinks;
      } else if (this.user?.hod) {
        this.links = this.hodLinks;
      } else if (this.isSuperadminLoggedSchool) {
        this.links = this.schoolLinks;
      } else if (this.hasRole(this.ROLES.SYSTEMADMINS) && !this.user.school) {
        this.links = this.systemAdminLinks;
      } else {
        this.links = this.schoolLinks;
      }

      this.resetSidebarState();
      eBus.$on("update-favourites", this.handleChangeFavourites);

    });
    eBus.$on("hodAsTeacher", this.resetSidebarState);
    eBus.$on("hodBackToMain", this.resetSidebarState);
  },

  methods: {
    resetSidebarState() {
      // Notify all dropdowns of school change
      eBus.$emit("school-changed");
      if (this.user?.teacher) {
        this.links = this.teachersLinks;
      } else if (this.user?.hod) {
        if (StorageService.getItem('teacher')) {
          this.links = this.teachersLinks;
        }
        else
          this.links = this.hodLinks;
      } else if (this.hasRole(this.ROLES.SYSTEMADMINS) && !this.user.school) {
        this.links = this.systemAdminLinks;
      } else {
        this.links = this.schoolLinks;
      }
    },

    showGroup(children) {
      for (let item of children) {
        if (this.can(this.PERMISSIONS[item.permission])) {
          return true;
        }
      }
      return false;
    },

    async fetchVersion() {
      try {
        const response = await VersionApi.getVersion();
        this.version = response.data.version;
      } catch (error) {
        console.error("Error fetching version:", error);
        this.version = "Unknown";
      }
    },

    closeAll(e) {
      let navLink = $(e.target).parents(".nav-item").find(".nav-link")[0];
      let collapseEl = $(e.target).parents(".nav-item").find(".collapse")[0];
      let collapseID = $(collapseEl).attr("id");

      $(".nav-link")
        .not(`[data-target = "${$(navLink).attr("data-target")}"]`)
        .addClass("collapsed");
      $(".collapse")
        .not("#" + collapseID)
        .removeClass("show");
    },

    async handleLanguageChange(newLocale) {
      console.log('Language changed to:', newLocale);
      if (this.currentLocale === newLocale) return;

      try {
        const isRTL = newLocale.startsWith('ar');
        const direction = isRTL ? 'rtl' : 'ltr';

        // 1. Update Lang and Service Headers
        this.saveLocale(newLocale);
        this.currentLocale = newLocale;
        localStorage.setItem("locale", newLocale);

        // 2. Update Backend Session (Silent)
        axios.post("/change-language", { locale: newLocale }, {
          headers: {
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
            "Content-Type": "application/json",
          },
        }).catch(err => console.error("Backend language sync failed:", err));

        // 3. Update DOM Attributes
        document.documentElement.setAttribute("lang", newLocale);
        document.documentElement.setAttribute("dir", direction);

        // 4. Dynamic Style Swapping (No Reload)
        const bootstrapLink = document.getElementById('bootstrap-link');
        const mainStyleLink = document.getElementById('main-style-link');
        let customEnLink = document.getElementById('custom-en-link');

        if (bootstrapLink) {
          bootstrapLink.href = isRTL
            ? 'https://cdn.rtlcss.com/bootstrap/v4.1.3/css/bootstrap.min.css'
            : 'https://cdn.jsdelivr.net/npm/bootstrap@4.1.3/dist/css/bootstrap.min.css';
        }

        if (mainStyleLink) {
          mainStyleLink.href = isRTL ? '/assets/css/style-ar.css' : '/assets/css/style-en.css';
        }

        const appCssLink = document.head.querySelector("link[data-app='vuejs']");
        if (appCssLink) {
          const currentHref = appCssLink.href;
          const targetSuffix = isRTL ? 'app-ar.css' : 'app-en.css';
          if (!currentHref.includes(targetSuffix)) {
            // This is a bit tricky with mix hashes, but if we assume the structure is consistent
            appCssLink.href = isRTL ? currentHref.replace('app-en', 'app-ar') : currentHref.replace('app-ar', 'app-en');
          }
        }

        if (!isRTL) {
          if (!customEnLink) {
            customEnLink = document.createElement('link');
            customEnLink.rel = 'stylesheet';
            customEnLink.id = 'custom-en-link';
            customEnLink.href = '/assets/front_end/css/new_style/custom_en.css';
            document.head.appendChild(customEnLink);
          }
        } else if (customEnLink) {
          customEnLink.remove();
        }

        // 5. Global Event to trigger re-renders in components
        eBus.$emit('locale-changed', newLocale);

        // Optional: If you still feel some parts need a reload for specific legacy reasons, 
        // you can keep it, but the user requested "not reloading".
        // window.location.reload(); 
      } catch (error) {
        console.error("Language switch failed:", error);
      }
    },

    async fetchLanguages() {
      try {
        const response = await Language.active();
        this.availableLanguages = response.data.data;
      } catch (error) {
        console.error("Error fetching languages:", error);
        // Fallback static list if API fails
        this.availableLanguages = [
          { code: 'ar', native_name: 'العربية', is_active: true, direction: 'rtl' },
          { code: 'ar_kw', native_name: 'العربية (الكويت)', is_active: true, direction: 'rtl' },
          { code: 'en', native_name: 'English', is_active: true, direction: 'ltr' }
        ];
      }
    },

    setLocale(lang) {
      this.saveLocale(lang);
      // this.changeDir(lang);
    },

    saveLocale(lang) {
      this.Lang.setLocale(lang);
      ApiService.setHeader("SMS-Locale", lang);
      StorageService.saveItem("locale", lang);
    },

    loadSavedLocale() {
      const savedLocale = localStorage.getItem('locale');
      if (savedLocale) {
        this.Lang.setLocale(savedLocale);
        ApiService.setHeader("SMS-Locale", savedLocale);
        document.documentElement.setAttribute('lang', savedLocale);
        document.documentElement.setAttribute('dir', savedLocale.startsWith('ar') ? 'rtl' : 'ltr');
      }
    },

    // favourites and history links
    getUserFavourites() {
      this.$emit("getUserFavourites");
    },

    getUserLinkHistories() {
      LinkHistory.list({
        page: 1,
        user: this.user.id,
      }).then((response) => {
        this.history_list = response.data.data.length ? response.data.data : [];
      });
    },

    addToFav(item) {
      Favorite.store(item)
        .then((res) => {
          eBus.$emit("update-favourites");
        })
        .catch((err) => {
          if (err.response && err.response.status === 400) {
            // Show the error message from backend
            this.$notify.error(err.response.data.message);
          } else {
            this.$notify.error("Something went wrong. Please try again.");
          }
        });
    },

    removeFromFav(item) {
      Favorite.delete(item).then((response) => {
        eBus.$emit("update-favourites");
      });
    },

    addToLinkHistory(item) {
      LinkHistory.store(item).then((res) => {
        this.$router.push({ path: item.path, query: item.query });
        this.getUserLinkHistories();
      });
    },

    removeSearchResult(item) {
      if (item.id) {
        LinkHistory.delete(item).then((response) => {
          this.getUserLinkHistories();
        });
      }
    },

    handleChangeFavourites() {
      this.getUserFavourites();
    },

    // NEW: Recursive method to check if any descendant has permission
    hasVisibleChildren(children) {
      if (!children || children.length === 0) {
        return false;
      }

      return children.some((child) => {
        // FIRST: Check descendants recursively
        if (child.children && child.children.length > 0) {
          const hasVisibleDescendants = this.hasVisibleChildren(child.children);
          if (hasVisibleDescendants) {
            return true; // Show parent if any descendant is visible
          }
        }

        const isPermissionArray = Array.isArray(child.permission);
        if (isPermissionArray) {
          const hasAnyPermission = child.permission.some(perm => this.can(perm));
          if (hasAnyPermission) return true;
        }
        return this.can(child.permission);
      });
    },
  },

  watch: {
    // Watch for both school access and user changes
    user: {
      handler(newVal, oldVal) {
        // Reset sidebar state when user or school changes
        this.resetSidebarState();
      },
      deep: true,
    },
    // Watch at superadmin logged as school admin
    isSuperadminLoggedSchool: {
      handler(newVal) {
        if (newVal) {
          this.resetSidebarState();
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    eBus.$off("superAdminAsSchoolAdmin");
    eBus.$off("update-favourites", this.handleChangeFavourites);
  },
};
</script>

<style scoped>
.sidebar-item-fav {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-item-fav .collapse-item {
  white-space: normal !important;
}

.fav-status {
  margin: 0 0.5rem;
  width: 1.2rem;
  height: 1.2rem;
  background: #cacaca;
  cursor: pointer;
  color: #828282;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  flex: 0 0 auto;
}

.fav-status.active {
  color: #fff;
  background-color: red;
}

.version-link:hover {
  color: #0a2e3b !important;
  font-weight: 600;
  transform: translateY(-2px);
}
</style>